import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ShieldCheck, Globe, Plane, Phone, ChevronDown } from "lucide-react";
import { WHATSAPP_LINK } from "@/data/constants";
import { useLang, LANGS } from "@/context/LangContext";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { t, lang, changeLang } = useLang();

  const links = [
    { href: "#vacantes", label: t("nav.vacantes") },
    { href: "#shein", label: t("nav.shein") },
    { href: "#proceso", label: t("nav.proceso") },
    { href: "#nosotros", label: t("nav.nosotros") },
    { href: "#empresas", label: t("nav.empresas") },
    { href: "#contacto", label: t("nav.contacto") },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const current = LANGS.find((l) => l.code === lang) || LANGS[0];

  const LangSwitch = () => (
    <div className="relative">
      <button data-testid="lang-switch" onClick={() => setLangOpen((o) => !o)}
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-200 hover:text-[#FFD166] border border-white/15 hover:border-[#FFD166]/50 rounded-full px-3 py-1.5 transition-colors">
        <Globe size={14} /> {current.flag} {current.label} <ChevronDown size={13} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
      </button>
      {langOpen && (
        <div className="absolute right-0 mt-2 bg-[#1C2541] border border-white/10 rounded-xl shadow-2xl overflow-hidden w-36 z-50">
          {LANGS.map((l) => (
            <button key={l.code} data-testid={`lang-option-${l.code}`}
              onClick={() => { changeLang(l.code); setLangOpen(false); }}
              className={`flex items-center gap-2 w-full px-4 py-2.5 text-sm text-left hover:bg-white/10 transition-colors ${lang === l.code ? "text-[#FFD166] bg-white/5" : "text-slate-200"}`}>
              <span className="text-base">{l.flag}</span> {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#0B132B]/95 shadow-[0_10px_40px_-12px_rgba(0,0,0,0.6)]" : "bg-transparent"
      }`}
    >
      {/* Utility bar */}
      <div className={`hidden md:block overflow-hidden transition-all duration-300 border-b border-white/10 ${scrolled ? "max-h-0 opacity-0" : "max-h-12 opacity-100"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-10 text-xs">
            <div className="flex items-center gap-4 text-slate-300">
              <span className="flex items-center gap-1.5">🇵🇱🇲🇽 {t("nav.tagline")}</span>
              <span className="text-white/15">|</span>
              <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="nav-utility-whatsapp"
                className="flex items-center gap-1.5 text-slate-200 hover:text-[#25D366] transition-colors font-medium">
                <Phone size={13} /> +48 512 287 255
              </a>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/admin/login" data-testid="nav-admin-link" className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors">
                <ShieldCheck size={13} /> {t("nav.admin")}
              </Link>
              <LangSwitch />
            </div>
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? "h-16" : "h-[68px]"}`}>
          <a href="#top" onClick={(e) => { e.preventDefault(); go("#top"); }} data-testid="nav-logo" className="flex items-center gap-2.5 group">
            <span className="w-9 h-9 rounded-xl bg-[#FFD166] flex items-center justify-center shadow-lg group-hover:rotate-6 transition-transform">
              <Plane size={18} className="text-[#0B132B]" />
            </span>
            <span className="text-2xl font-head font-extrabold tracking-tight text-white leading-none">
              europe<span className="text-[#FFD166]">jobs</span>
            </span>
          </a>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                onClick={() => go(l.href)}
                className="link-underline text-sm font-medium text-slate-200 hover:text-white transition-colors"
              >
                {l.label}
              </button>
            ))}
            <Link to="/polonia" data-testid="nav-link-polonia" className="link-underline text-sm font-semibold text-[#FFD166] hover:text-white transition-colors">{t("nav.polonia")}</Link>
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            <LangSwitch />
            <button
              onClick={() => go("#aplicar")}
              data-testid="nav-apply-btn"
              className="bg-[#FFD166] text-[#0B132B] font-bold text-sm px-6 py-2.5 rounded-full hover:bg-[#E0A926] hover:shadow-[0_8px_24px_-6px_rgba(255,209,102,0.6)] transition-all"
            >
              {t("nav.apply")}
            </button>
          </div>

          <div className="lg:hidden flex items-center gap-3">
            <LangSwitch />
            <button className="text-white" data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)}>
              {open ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0B132B] border-t border-white/10 px-4 py-4 space-y-1">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-mobile-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="block w-full text-left py-2.5 text-slate-200 hover:text-[#FFD166] border-b border-white/5"
            >
              {l.label}
            </button>
          ))}
          <Link to="/polonia" onClick={() => setOpen(false)} data-testid="nav-mobile-polonia" className="block w-full text-left py-2.5 text-[#FFD166] font-semibold border-b border-white/5">{t("nav.polonia")}</Link>
          <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="flex items-center gap-2 py-2.5 text-[#25D366] font-semibold">
            <Phone size={15} /> +48 512 287 255
          </a>
          <Link to="/admin/login" className="flex items-center gap-2 py-2.5 text-slate-300">
            <ShieldCheck size={15} /> {t("nav.admin")}
          </Link>
          <button
            onClick={() => go("#aplicar")}
            className="w-full bg-[#FFD166] text-[#0B132B] font-bold px-5 py-3 rounded-full mt-2"
          >
            {t("nav.apply")}
          </button>
        </div>
      )}
    </header>
  );
};
